import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, Target, Sparkles, ArrowUpRight } from "lucide-react";
import { useLifeState } from "@/context/LifeStateContext";
import { areas, kinds, cadences, areaMatch, areaEncouragement, uid, cap, computeLifeMetrics, iso, kindsForArea, kindTone } from "@/lib/life";
import { fmtMoney } from "@/lib/finance";
import { PageHeader, SectionCard, EmptyState, AddButton, ProgressBar, StatusPill } from "@/components/shared";
import { FormModal, TextField, SelectField, TextArea } from "@/components/forms";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const cadenceOpts = cadences.map((c) => ({ value: c, label: c }));
const areaOpts = areas.map((a) => ({ value: a.name, label: a.name }));

export default function LifeAreas() {
  const navigate = useNavigate();
  const { state, update, loading, wealthStewardship, wellnessVitality } = useLifeState();
  const [selected, setSelected] = useState(areas[0].name);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(null);

  if (loading || !state) return <Skeleton className="h-96 rounded-[14px]" />;

  const overrides = {};
  if (wealthStewardship) overrides["Wealth & Stewardship"] = wealthStewardship.score;
  if (wellnessVitality) overrides["Health & Vitality"] = wellnessVitality.score;
  const m = computeLifeMetrics(state, iso(), overrides);
  const scoreFor = (name) => m.areaScores.find((s) => s.area.name === name)?.score ?? 0;
  const area = areas.find((a) => a.name === selected);
  const enc = areaEncouragement[selected];
  const items = (state.components || []).filter((c) => areaMatch(c.area, area));
  const goals = (state.goals || []).filter((g) => areaMatch(g.engine, area));

  const openNew = () => { setDraft({ id: uid("c"), area: selected, kind: kindsForArea(selected)[0], title: "", detail: "", cadence: "Weekly", progress: 0 }); setOpen(true); };
  const openEdit = (c) => { setDraft({ ...c }); setOpen(true); };
  const save = () => {
    if (!draft.title.trim()) return toast.error("Title is required");
    const item = { ...draft, title: draft.title.trim(), progress: cap(Number(draft.progress)) };
    update((prev) => {
      const exists = (prev.components || []).some((x) => x.id === item.id);
      return { ...prev, components: exists ? prev.components.map((x) => (x.id === item.id ? item : x)) : [...(prev.components || []), item] };
    }, "Updated life blueprint");
    setOpen(false); toast.success("Blueprint saved");
  };
  const remove = (id) => { update((prev) => ({ ...prev, components: prev.components.filter((c) => c.id !== id) }), "Removed blueprint"); toast.success("Removed"); };
  const setProgress = (id, v) => update((prev) => ({ ...prev, components: prev.components.map((c) => (c.id === id ? { ...c, progress: v } : c)) }), "Updated progress");

  return (
    <div>
      <PageHeader title="Life Areas" subtitle="Eight operating blueprints for a whole, well-stewarded life." testid="life-areas-header" />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="areas-grid">
        {areas.map((a) => {
          const score = scoreFor(a.name);
          const active = a.name === selected;
          const Icon = a.icon;
          return (
            <button key={a.name} onClick={() => setSelected(a.name)} data-testid={`area-card-${a.short}`}
              className={cn("text-left rounded-[14px] border bg-[var(--surface-1)] p-4 transition-shadow hover:shadow-[var(--shadow-md)]", active ? "border-[color:rgba(31,115,85,0.5)] shadow-[var(--shadow-sm)]" : "border-[color:var(--border-subtle)]")}>
              <div className="flex items-center justify-between">
                <span className="grid place-items-center h-9 w-9 rounded-[10px] text-white" style={{ backgroundColor: a.color }}><Icon className="h-4 w-4" /></span>
                <span className="text-sm tabular-nums font-medium text-[var(--text)]">{score}</span>
              </div>
              <div className="text-sm font-medium text-[var(--text)] mt-2 leading-tight">{a.name}</div>
              <div className="mt-2"><ProgressBar value={score} max={100} tone={score > 50 ? "green" : "gold"} /></div>
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-8 space-y-4">
          <SectionCard testid="area-question-card">
            <div className="flex items-start gap-3">
              <span className="grid place-items-center h-11 w-11 rounded-[12px] text-white shrink-0" style={{ backgroundColor: area.color }}><area.icon className="h-5 w-5" /></span>
              <div><div className="text-[11px] uppercase tracking-[0.16em] text-[var(--text-muted)]">{area.short}</div><h3 className="font-serif-display text-xl text-[var(--text)]">{area.name}</h3><p className="text-sm text-[var(--text-muted)] mt-1">{area.question}</p></div>
            </div>
          </SectionCard>

          {selected === "Wealth & Stewardship" && wealthStewardship && (
            <SectionCard testid="wealth-live-card" title="Live from your Wealth Command Center" subtitle="This score is calculated from your real financial data"
              right={<Button variant="outline" size="sm" onClick={() => navigate("/wealth")} data-testid="open-wealth-button" className="border-[color:var(--border-subtle)]"><ArrowUpRight className="h-3.5 w-3.5 mr-1" />Open Wealth</Button>}>
              <div className="flex items-center gap-4 mb-4">
                <div className="font-serif-display text-4xl text-[var(--green)] tabular-nums" data-testid="stewardship-score">{wealthStewardship.score}</div>
                <div className="text-sm text-[var(--text-muted)]">
                  <div>Net worth <span className="text-[var(--text)] font-medium">{fmtMoney(wealthStewardship.netWorth)}</span></div>
                  <div className="mt-0.5">{wealthStewardship.months} mo emergency fund · {wealthStewardship.util}% utilization · {wealthStewardship.savingsRate}% saved</div>
                </div>
              </div>
              <div className="space-y-2.5">
                {[["Emergency fund", "emergencyFund"], ["Credit utilization", "utilization"], ["Savings rate", "savingsRate"], ["Debt load", "debtLoad"], ["Goals progress", "goals"]].map(([lbl, k]) => (
                  wealthStewardship.breakdown[k] == null ? null : (
                    <div key={k} className="flex items-center gap-3" data-testid={`stewardship-${k}`}>
                      <span className="text-[12px] text-[var(--text-muted)] w-32 shrink-0">{lbl}</span>
                      <div className="flex-1"><ProgressBar value={wealthStewardship.breakdown[k]} max={100} tone={wealthStewardship.breakdown[k] > 50 ? "green" : "gold"} /></div>
                      <span className="text-xs tabular-nums text-[var(--text)] w-9 text-right">{wealthStewardship.breakdown[k]}</span>
                    </div>
                  )
                ))}
              </div>
              <p className="text-[11px] text-[var(--text-faint)] mt-4">Blueprints below still let you add faith-driven stewardship routines and projects.</p>
            </SectionCard>
          )}

          {selected === "Health & Vitality" && wellnessVitality && (
            <SectionCard testid="wellness-live-card" title="Live from your Wellness Command Center" subtitle="This score is calculated from your real wellness data"
              right={<Button variant="outline" size="sm" onClick={() => navigate("/wellness")} data-testid="open-wellness-button" className="border-[color:var(--border-subtle)]"><ArrowUpRight className="h-3.5 w-3.5 mr-1" />Open Wellness</Button>}>
              <div className="flex items-center gap-4 mb-4">
                <div className="font-serif-display text-4xl text-[var(--green)] tabular-nums" data-testid="vitality-score">{wellnessVitality.score}</div>
                <div className="text-sm text-[var(--text-muted)]">
                  <div>Goal progress <span className="text-[var(--text)] font-medium">{wellnessVitality.goalAvg}%</span></div>
                  <div className="mt-0.5">{wellnessVitality.latestWeight ? `${wellnessVitality.latestWeight} lb` : "—"} · energy {wellnessVitality.energy || "—"}/10 · readiness {wellnessVitality.readiness}</div>
                </div>
              </div>
              <div className="space-y-2.5">
                {[["Daily readiness", "readiness"], ["Goals", "goals"], ["Sleep", "sleep"], ["Workouts", "workouts"], ["Glow ritual", "glow"], ["Hydration", "hydration"]].map(([lbl, k]) => (
                  <div key={k} className="flex items-center gap-3" data-testid={`vitality-${k}`}>
                    <span className="text-[12px] text-[var(--text-muted)] w-32 shrink-0">{lbl}</span>
                    <div className="flex-1"><ProgressBar value={wellnessVitality.breakdown[k]} max={100} tone={wellnessVitality.breakdown[k] > 50 ? "green" : "gold"} /></div>
                    <span className="text-xs tabular-nums text-[var(--text)] w-9 text-right">{wellnessVitality.breakdown[k]}</span>
                  </div>
                ))}
              </div>
            </SectionCard>
          )}

          <SectionCard title="Blueprints" subtitle="Routines, projects, metrics and milestones for this area" testid="area-blueprints-card"
            right={<AddButton onClick={openNew} testid="add-blueprint-button"><Plus className="h-4 w-4 mr-1.5" />Add</AddButton>}>
            {items.length === 0 ? (
              <EmptyState icon={Target} title="No blueprints yet" message="Add a routine, project or metric to operate this area with intention." />
            ) : (
              <div className="space-y-2">
                {items.map((c) => (
                  <div key={c.id} className="rounded-[12px] border border-[color:var(--border-subtle)] bg-white p-3" data-testid={`blueprint-row-${c.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0"><div className="flex items-center gap-2"><StatusPill status={kindTone(c.kind)} /><span className="text-sm font-medium text-[var(--text)] truncate">{c.title}</span><span className="text-[10px] uppercase tracking-wide text-[var(--text-faint)]">{c.kind}</span></div><p className="text-[12px] text-[var(--text-muted)] mt-1">{c.detail}</p></div>
                      <div className="flex items-center gap-1 shrink-0">
                        <span className="text-[11px] text-[var(--text-faint)]">{c.cadence}</span>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(c)} data-testid={`blueprint-edit-${c.id}`} aria-label="Edit"><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-[var(--danger)]" onClick={() => remove(c.id)} data-testid={`blueprint-delete-${c.id}`} aria-label="Delete"><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 mt-2">
                      <div className="flex-1"><Slider value={[c.progress]} min={0} max={100} step={1} onValueChange={(v) => setProgress(c.id, v[0])} data-testid={`blueprint-progress-${c.id}`} /></div>
                      <span className="text-xs tabular-nums text-[var(--text)] w-9 text-right">{c.progress}%</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>

          {goals.length > 0 && (
            <SectionCard title="Goals in this area" testid="area-goals-card">
              <div className="space-y-2">
                {goals.map((g) => (
                  <div key={g.id} className="rounded-[12px] border border-[color:var(--border-subtle)] bg-white p-3" data-testid={`area-goal-${g.id}`}>
                    <div className="flex items-center justify-between gap-2"><span className="text-sm font-medium text-[var(--text)]">{g.title}</span><span className="text-xs text-[var(--text-faint)]">{g.due}</span></div>
                    <div className="flex items-center gap-3 mt-2"><div className="flex-1"><ProgressBar value={g.progress} max={100} tone="green" /></div><span className="text-xs tabular-nums text-[var(--text)]">{g.progress}%</span></div>
                  </div>
                ))}
              </div>
            </SectionCard>
          )}
        </div>

        <div className="lg:col-span-4">
          {enc && (
            <div className="rounded-[var(--radius-card)] p-5 text-[#f7fbf9] sticky top-20" style={{ background: "linear-gradient(135deg,#c68b3c,#8a5a17)" }} data-testid="area-encouragement">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] text-[#fbeecf]"><Sparkles className="h-3.5 w-3.5" />Encouragement</div>
              <h4 className="font-serif-display text-lg mt-3">{enc.title}</h4>
              <p className="text-sm text-[#fbeecf] mt-2 leading-relaxed">{enc.message}</p>
              <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#ffffff22] px-3 py-1.5 text-[12px] font-medium">{enc.action}</div>
            </div>
          )}
        </div>
      </div>

      {draft && (
        <FormModal open={open} onOpenChange={setOpen} title={(state.components || []).some((x) => x.id === draft.id) ? "Edit blueprint" : "Add blueprint"} onSubmit={save} testid="blueprint-modal">
          <TextField label="Title" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} full testid="blueprint-title-input" placeholder="e.g. Morning devotion" />
          <SelectField label="Life area" value={draft.area} onChange={(v) => setDraft({ ...draft, area: v, kind: kindsForArea(v)[0] })} options={areaOpts} testid="blueprint-area-select" />
          <SelectField label="Type" value={draft.kind} onChange={(v) => setDraft({ ...draft, kind: v })} options={kindsForArea(draft.area).map((k) => ({ value: k, label: k }))} testid="blueprint-kind-select" />
          <SelectField label="Cadence" value={draft.cadence} onChange={(v) => setDraft({ ...draft, cadence: v })} options={cadenceOpts} testid="blueprint-cadence-select" />
          <TextArea label="Detail" value={draft.detail} onChange={(e) => setDraft({ ...draft, detail: e.target.value })} full testid="blueprint-detail-input" />
        </FormModal>
      )}
    </div>
  );
}
